import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  Plus, Loader2, AlertTriangle, PencilLine, Link2, Link2Off, Copy, QrCode, Users, Check, X, Download, Archive, Printer,
  LayoutTemplate, RefreshCw, BookmarkPlus, Trash2,
} from 'lucide-react';
import { SifaTeilnahme, SifaUnterweisung, SifaUnterweisungVorlage } from '../../services/sifa/types';
import { fassungVeraltet } from '../../services/sifa/vorlagenLogic';
import {
  STATUS_LABEL,
  TeilnahmenZaehler,
  darfAktivieren,
  entwurfAus,
  gueltigkeitText,
  linkIstAktiv,
  linkUrl,
  qrDateiname,
  aushangDateiname,
  aushangVorgabe,
  zaehleJeUnterweisung,
} from './unterweisungModulLogic';
import { erzeugeAushangPdf } from '../../services/sifa/aushangPdf';
import { ensureEmbeddedFont } from '../../services/pdf/embeddedFont';
import { describeError } from '../../services/errorMessage';
import { herkunftText, neueFassungText, uebernahmeFrage } from './neueUnterweisungLogic';
import { entwurfLoeschStand, loeschFrage } from './unterweisungAblageLogic';
import { RueckfrageDialog } from './vorlagen/RueckfrageDialog';

interface Props {
  unterweisungen: SifaUnterweisung[];
  teilnahmen: SifaTeilnahme[];
  laden: boolean;
  fehler: string | null;
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Laufender Vorgang (anlegen). */
  busy?: boolean;
  /** Unterweisung, deren Link gerade umgeschaltet wird. */
  linkBusyId?: string | null;
  onNeu: () => void;
  onBearbeiten: (u: SifaUnterweisung) => void;
  onTeilnahmen: (u: SifaUnterweisung) => void;
  onLinkSchalten: (u: SifaUnterweisung, aktiv: boolean) => void;
  /** Betreuendes Buero (Fusszeile des Aushangs), z. B. „GefAS mbH". */
  bueroName?: string;
  /** Kunde = Projektname, steht klein ueber der Ueberschrift des Aushangs. */
  kundeName?: string;
  /**
   * Vorlagen der Firma nach id (Nr. 141), einmal vom Modul geladen — fuer den
   * Abgleich „Neue Fassung verfuegbar". Leer oder ohne Eintrag: nur die Marke
   * „aus Vorlage", keine Behauptung ueber eine neuere Fassung.
   */
  vorlagen?: ReadonlyMap<string, SifaUnterweisungVorlage>;
  /** Darf „Als Vorlage speichern" (Admin oder Haekchen „SiFa"). */
  darfAlsVorlageSpeichern?: boolean;
  /** Unterweisung, in die gerade eine neue Fassung uebernommen wird. */
  uebernehmenBusyId?: string | null;
  /** Unterweisung, die gerade als Vorlage gespeichert wird. */
  alsVorlageBusyId?: string | null;
  /** Neue Fassung uebernehmen — kommt erst NACH der Rueckfrage in dieser Liste. */
  onNeueFassungUebernehmen?: (u: SifaUnterweisung, v: SifaUnterweisungVorlage) => void;
  /** Kunden-Unterweisung als neuen Vorlagen-Entwurf speichern. */
  onAlsVorlageSpeichern?: (u: SifaUnterweisung) => void;
  /** Entwurf, der gerade geloescht wird. */
  loeschenBusyId?: string | null;
  /**
   * Entwurf loeschen (Nr. 143) — kommt erst NACH der Rueckfrage in dieser
   * Liste. Ohne Handler kein Knopf.
   */
  onEntwurfLoeschen?: (u: SifaUnterweisung) => void;
}

const LEER_ZAEHLER: TeilnahmenZaehler = { gesamt: 0, bestanden: 0, nichtBestanden: 0, offen: 0, ohneMitarbeiter: 0, unabgerechnet: 0 };

/**
 * Die Ablage: alle Unterweisungen (Kurse) dieses Projekts als Karten.
 *
 * Ein SiFa-Kunde hat MEHRERE Kurse (Brandschutz, Erste Hilfe, Gefahrstoffe,
 * Stapler …), jeder mit eigenem Kundenlink. Die Karte zeigt, was der
 * Innendienst wissen muss, ohne zu klicken: Status, Teilnahmen, Gueltigkeit —
 * und die vier Handgriffe, mit denen der Link zum Kunden kommt.
 *
 * Der Link selbst bleibt sichtbar kopierbar; das QR-Bild wird erst auf
 * Knopfdruck erzeugt (qrcode) und liegt dann als PNG zum Herunterladen — fuer
 * den Aushang am Schwarzen Brett des Kunden.
 */
export const UnterweisungListe: React.FC<Props> = ({
  unterweisungen,
  teilnahmen,
  laden,
  fehler,
  isReadOnly = false,
  isMobile = false,
  busy = false,
  linkBusyId = null,
  onNeu,
  onBearbeiten,
  onTeilnahmen,
  onLinkSchalten, bueroName, kundeName,
  vorlagen,
  darfAlsVorlageSpeichern = false,
  uebernehmenBusyId = null,
  alsVorlageBusyId = null,
  onNeueFassungUebernehmen,
  onAlsVorlageSpeichern,
  loeschenBusyId = null,
  onEntwurfLoeschen }) => {
  const aktion = 'min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40';
  const zaehler = zaehleJeUnterweisung(teilnahmen);
  const [kopiertId, setKopiertId] = useState<string | null>(null);
  const [qrFuer, setQrFuer] = useState<SifaUnterweisung | null>(null);
  const [uebernahmeFuer, setUebernahmeFuer] = useState<{ u: SifaUnterweisung; v: SifaUnterweisungVorlage } | null>(null);
  const [loeschenFuer, setLoeschenFuer] = useState<SifaUnterweisung | null>(null);

  const kundenlink = (u: SifaUnterweisung) =>
    u.linkToken ? linkUrl(window.location.origin, window.location.pathname, u.linkToken) : null;
  // setzeLink(…, true) setzt den Status selbst auf aktiv; die Pruefung hier
  // ist dieselbe wie im Editor („Aktiv schalten"), nur ohne Umweg dorthin.
  const kannAktiv = (u: SifaUnterweisung) => u.status === 'aktiv' || darfAktivieren(entwurfAus(u));

  const kopieren = async (u: SifaUnterweisung) => {
    const url = kundenlink(u);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setKopiertId(u.id);
      window.setTimeout(() => setKopiertId((id) => (id === u.id ? null : id)), 2000);
    } catch {
      // Zwischenablage gesperrt (unsicherer Kontext): der Link steht unten auf der Karte zum Markieren.
      window.prompt('Link kopieren:', url);
    }
  };

  const statusChip = (u: SifaUnterweisung) => {
    const ton = u.status === 'aktiv'
      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : u.status === 'archiviert'
        ? 'text-slate-600 bg-slate-100 border-slate-200'
        : 'text-amber-700 bg-amber-50 border-amber-200';
    const Icon = u.status === 'aktiv' ? Check : u.status === 'archiviert' ? Archive : PencilLine;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold border rounded-full ${ton}`}>
        <Icon className="w-3 h-3" /> {STATUS_LABEL[u.status]}
      </span>
    );
  };

  const linkChip = (u: SifaUnterweisung) => (
    linkIstAktiv(u) ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full">
        <Link2 className="w-3 h-3" /> Link aktiv
      </span>
    ) : u.linkToken ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full">
        <Link2Off className="w-3 h-3" /> Link aus
      </span>
    ) : null
  );

  const aktionen = (u: SifaUnterweisung) => {
    const aktiv = linkIstAktiv(u);
    const url = kundenlink(u);
    const loeschen = entwurfLoeschStand(u, (zaehler[u.id] || LEER_ZAEHLER).gesamt, isReadOnly);
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => onBearbeiten(u)} className={`${aktion} text-blue-700 bg-blue-50 hover:bg-blue-100`}>
            <PencilLine className="w-3.5 h-3.5" /> {isReadOnly || u.status === 'archiviert' ? 'Ansehen' : 'Bearbeiten'}
          </button>
          <button onClick={() => onTeilnahmen(u)} className={`${aktion} text-slate-700 bg-slate-100 hover:bg-slate-200`}>
            <Users className="w-3.5 h-3.5" /> Teilnahmen
          </button>
          {u.status !== 'archiviert' && (
            <button
              onClick={() => onLinkSchalten(u, !aktiv)}
              disabled={isReadOnly || linkBusyId === u.id || (!aktiv && !kannAktiv(u))}
              title={!aktiv && !kannAktiv(u) ? 'Der Kurs braucht Titel und mindestens einen Abschnitt (siehe Editor), bevor der Link aktiv werden kann.' : undefined}
              className={`${aktion} ${aktiv ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-teal-700 bg-teal-50 hover:bg-teal-100'}`}
            >
              {linkBusyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : aktiv ? <Link2Off className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
              {aktiv ? 'Link deaktivieren' : 'Link aktivieren'}
            </button>
          )}
          {url && (
            <>
              <button onClick={() => void kopieren(u)} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                {kopiertId === u.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {kopiertId === u.id ? 'Kopiert' : 'Link kopieren'}
              </button>
              <button onClick={() => setQrFuer(u)} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                <QrCode className="w-3.5 h-3.5" /> QR anzeigen
              </button>
            </>
          )}
          {/* Auch in der Nur-Ansicht: es entsteht eine Vorlage, am Projekt aendert sich nichts. */}
          {darfAlsVorlageSpeichern && onAlsVorlageSpeichern && (
            <button
              onClick={() => onAlsVorlageSpeichern(u)}
              disabled={!!alsVorlageBusyId}
              title="Legt aus dieser Unterweisung einen Vorlagen-Entwurf für alle Kunden an. Die Kundenfassung bleibt unverändert."
              className={`${aktion} text-violet-700 bg-violet-50 hover:bg-violet-100`}
            >
              {alsVorlageBusyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
              Als Vorlage speichern
            </button>
          )}
          {loeschen.zeigen && onEntwurfLoeschen && (
            <button
              onClick={() => setLoeschenFuer(u)}
              disabled={!!loeschen.gesperrtGrund || !!loeschenBusyId}
              title={loeschen.gesperrtGrund ?? 'Entwurf samt eigenen Bildern und Videos löschen.'}
              className={`${aktion} text-red-700 bg-red-50 hover:bg-red-100`}
            >
              {loeschenBusyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Löschen
            </button>
          )}
        </div>
        {/* Als Zeile, nicht nur als Tooltip: am Handy gibt es keinen. */}
        {loeschen.zeigen && onEntwurfLoeschen && loeschen.gesperrtGrund && (
          <p className="text-[11px] text-slate-500">{loeschen.gesperrtGrund}</p>
        )}
      </div>
    );
  };

  /** Herkunftsmarke und — bei veralteter Fassung — der Hinweis mit „Übernehmen". */
  const herkunft = (u: SifaUnterweisung) => {
    const marke = herkunftText(u);
    if (!marke) return null;
    const v = u.vorlageId ? vorlagen?.get(u.vorlageId) ?? null : null;
    const veraltet = !!v && fassungVeraltet(u, v);
    return (
      <div className="space-y-1.5">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-full"
          title={v ? `Vorlage „${v.titel}"` : undefined}
        >
          <LayoutTemplate className="w-3 h-3" /> {marke}
        </span>
        {veraltet && v && (
          <div className="p-2 pl-3 bg-orange-50 border border-orange-200 rounded-xl flex flex-wrap items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 text-orange-600 shrink-0" />
            <span className="flex-1 min-w-0 text-xs font-bold text-orange-800">{neueFassungText(v)}</span>
            {!isReadOnly && u.status !== 'archiviert' && onNeueFassungUebernehmen && (
              <button
                onClick={() => setUebernahmeFuer({ u, v })}
                disabled={!!uebernehmenBusyId}
                className={`${aktion} text-white bg-orange-600 hover:bg-orange-700`}
              >
                {uebernehmenBusyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Übernehmen
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Unterweisungen</h2>
          <p className="text-xs text-slate-500">
            {unterweisungen.length === 0 ? 'Noch keine Unterweisung angelegt' : `${unterweisungen.length} angelegt`}
          </p>
        </div>
        <button
          onClick={onNeu}
          disabled={isReadOnly || busy}
          title={isReadOnly ? 'Nur-Ansicht: keine neue Unterweisung' : undefined}
          className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Neue Unterweisung
        </button>
      </div>

      {fehler && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
        </div>
      )}

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
      ) : unterweisungen.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">Noch keine Unterweisung</p>
          <p className="text-xs text-slate-500 mt-1">
            „Neue Unterweisung" legt einen Kurs an: Abschnitte zum Lesen, Fragen zur Lernerfolgskontrolle.
            Aktiv geschaltet bekommt der Kunde einen Link, ueber den die Mitarbeiter die Unterweisung selbst durchfuehren.
          </p>
        </div>
      ) : (
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {unterweisungen.map((u) => {
            const z = zaehler[u.id] || LEER_ZAEHLER;
            const url = kundenlink(u);
            return (
              <div key={u.id} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">Nr. {u.nummer} — {u.titel || 'ohne Titel'}</p>
                    {u.beschreibung && <p className="text-xs text-slate-500 line-clamp-2">{u.beschreibung}</p>}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">{statusChip(u)}{linkChip(u)}</div>
                </div>

                {herkunft(u)}

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-50 rounded-lg py-1.5">
                    <p className="text-base font-black text-slate-800">{z.gesamt}</p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Teilnahmen</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg py-1.5">
                    <p className="text-base font-black text-emerald-700">{z.bestanden}</p>
                    <p className="text-[10px] uppercase tracking-wide text-emerald-700/80">Bestanden</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg py-1.5">
                    <p className="text-base font-black text-amber-700">{z.offen}</p>
                    <p className="text-[10px] uppercase tracking-wide text-amber-700/80">Offen</p>
                  </div>
                </div>

                <p className="text-[11px] text-slate-500">
                  Gültigkeit {gueltigkeitText(u.gueltigMonate)} · Bestehensgrenze {u.bestehenProzent} %
                  {u.dauerMin ? ` · ca. ${u.dauerMin} min` : ''} · {u.inhalt.length} Abschnitte, {u.fragen.length} Fragen
                  {z.unabgerechnet > 0 ? ` · ${z.unabgerechnet} nicht abgerechnet` : ''}
                </p>

                {aktionen(u)}

                {url && (
                  <p className="text-[10px] text-slate-400 break-all select-all" title="Kundenlink">{url}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {qrFuer && <QrDialog unterweisung={qrFuer} url={kundenlink(qrFuer) || ''} bueroName={bueroName || ''} kundeName={kundeName || ''} onClose={() => setQrFuer(null)} />}

      {uebernahmeFuer && (
        <UebernahmeDialog
          unterweisung={uebernahmeFuer.u}
          vorlage={uebernahmeFuer.v}
          onClose={() => setUebernahmeFuer(null)}
          onBestaetigt={() => {
            const { u, v } = uebernahmeFuer;
            setUebernahmeFuer(null);
            onNeueFassungUebernehmen?.(u, v);
          }}
        />
      )}

      {loeschenFuer && (
        <RueckfrageDialog
          titel={`Entwurf Nr. ${loeschenFuer.nummer} löschen`}
          text={`${loeschFrage(loeschenFuer)}\n\nDas lässt sich nicht rückgängig machen.`}
          jaText="Löschen"
          gefaehrlich
          onNein={() => setLoeschenFuer(null)}
          onJa={() => {
            const u = loeschenFuer;
            setLoeschenFuer(null);
            onEntwurfLoeschen?.(u);
          }}
        />
      )}
    </div>
  );
};

/**
 * Rueckfrage vor „Neue Fassung übernehmen" — in der App statt im Browser,
 * weil sie sagen muss, was verloren geht (eigene Aenderungen) und was bleibt
 * (Link, Nummer, Teilnahmen). Der Fortschritt laeuft danach an der Karte.
 */
const UebernahmeDialog: React.FC<{
  unterweisung: SifaUnterweisung;
  vorlage: SifaUnterweisungVorlage;
  onClose: () => void;
  onBestaetigt: () => void;
}> = ({ unterweisung, vorlage, onClose, onBestaetigt }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800">Neue Fassung übernehmen</h3>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-slate-700 font-semibold">Nr. {unterweisung.nummer} — {unterweisung.titel || 'ohne Titel'}</p>
        <p className="text-xs text-slate-600">{uebernahmeFrage(vorlage)}</p>
      </div>
      <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
        <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
        <button onClick={onBestaetigt} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-xl">
          <RefreshCw className="w-4 h-4" /> Übernehmen
        </button>
      </div>
    </div>
  </div>
);

/**
 * QR-Code zum Kundenlink — als PNG fuer E-Mail und Intranet, und als AUSHANG
 * (A4-PDF mit Ueberschrift, Kurztext und grossem Code) fuer das Schwarze
 * Brett. Das Bild wird hier erzeugt (nicht im Container), damit die
 * Bibliothek erst laeuft, wenn jemand den Knopf drueckt.
 *
 * Ueberschrift und Text sind vorbelegt (aushangVorgabe) und vor dem Druck
 * frei aenderbar; gespeichert werden sie nicht — der Aushang ist ein
 * Druckerzeugnis, kein Datensatz.
 */
const QrDialog: React.FC<{ unterweisung: SifaUnterweisung; url: string; bueroName: string; kundeName: string; onClose: () => void }> = ({ unterweisung, url, bueroName, kundeName, onClose }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vorgabe] = useState(() => aushangVorgabe(unterweisung));
  const [ueberschrift, setUeberschrift] = useState(vorgabe.ueberschrift);
  const [text, setText] = useState(vorgabe.text);
  const [druckLaeuft, setDruckLaeuft] = useState(false);

  useEffect(() => {
    let aktiv = true;
    QRCode.toDataURL(url, { width: 480, margin: 2 })
      .then((d) => { if (aktiv) setDataUrl(d); })
      .catch((e) => { if (aktiv) setFehler(String(e?.message || e)); });
    return () => { aktiv = false; };
  }, [url]);

  const aushangErzeugen = async () => {
    setDruckLaeuft(true);
    setFehler(null);
    try {
      // Eigener, groesserer Code fuer den Druck: 480 px reichen fuer den
      // Bildschirm, auf 12 cm Papier wuerden sie weich.
      const qrDruck = await QRCode.toDataURL(url, { width: 1200, margin: 1 });
      await ensureEmbeddedFont();
      const bytes = erzeugeAushangPdf({
        ueberschrift,
        text,
        url,
        qrDataUrl: qrDruck,
        bueroName,
        kundeName,
        unterweisungNummer: unterweisung.nummer,
        unterweisungTitel: unterweisung.titel,
      });
      const { saveAs } = await import('file-saver');
      saveAs(new Blob([bytes], { type: 'application/pdf' }), aushangDateiname(unterweisung));
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setDruckLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">QR-Code zum Kundenlink</h3>
            <p className="text-xs text-slate-500 truncate">Nr. {unterweisung.nummer} — {unterweisung.titel}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
          <div className="space-y-2">
            {fehler ? (
              <p className="text-xs text-red-700">{fehler}</p>
            ) : dataUrl ? (
              <img src={dataUrl} alt="QR-Code" className="w-full max-w-[200px] mx-auto rounded-lg border border-slate-200" />
            ) : (
              <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
            )}
            <p className="text-[10px] text-slate-400 break-all select-all text-center">{url}</p>
            {dataUrl && (
              <a
                href={dataUrl}
                download={qrDateiname(unterweisung)}
                className="min-h-[40px] w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" /> Nur QR als PNG
              </a>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Aushang (A4)</p>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Überschrift</span>
              <input
                value={ueberschrift}
                onChange={(e) => setUeberschrift(e.target.value)}
                maxLength={120}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Kurzer Text</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                maxLength={600}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200 resize-y"
              />
              <span className="block text-[10px] text-slate-400 mt-1">Leerzeile = neuer Absatz. Der Link steht zusätzlich als Text unter dem Code.</span>
            </label>
            <button
              type="button"
              onClick={() => void aushangErzeugen()}
              disabled={druckLaeuft || !url}
              className="min-h-[44px] w-full flex items-center justify-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-xl transition-colors"
            >
              {druckLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Aushang als PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
